import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { CharacterCard } from '../types';
import { processFirestoreCardsLight } from '../utils/cardUtils';
import { sortCardsForCalendar } from '../utils/cardDateUtils';
import { OWNER_UID } from '../constants/owner';
import { useTheme } from '../contexts/ThemeContext';
import Header from './Header';
import Card from './Card';
import SkeletonCard from './SkeletonCard';
import { navigate } from './Router';
import { COLOR_GROUPS, classifyColor } from '../utils/colorUtils';

// ランダム選択
const pickRandom = <T,>(arr: T[], n: number): T[] => {
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, n);
};

interface ColorPageProps {
  colorName: string;
}

const ColorPage: React.FC<ColorPageProps> = ({ colorName }) => {
  const [allCards, setAllCards] = useState<CharacterCard[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { theme } = useTheme();

  // 色グループ → カードのマッピング
  const colorCardsMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    allCards.forEach(card => {
      card.characterData?.mainColors?.forEach(hex => {
        const group = classifyColor(hex);
        if (!map.has(group)) map.set(group, new Set());
        map.get(group)!.add(card.id);
      });
    });
    return map;
  }, [allCards]);

  // 色グループ一覧（カードがある色のみ、カウント順 — ショーケース用）
  const colorGroupsByCount = useMemo(() => {
    return COLOR_GROUPS
      .filter(g => (colorCardsMap.get(g.name)?.size || 0) >= 1)
      .map(g => ({ ...g, count: colorCardsMap.get(g.name)?.size || 0 }))
      .sort((a, b) => b.count - a.count);
  }, [colorCardsMap]);

  // 色相環順（All Colors表示用）
  const colorGroupsHue = useMemo(() => {
    return COLOR_GROUPS
      .filter(g => (colorCardsMap.get(g.name)?.size || 0) >= 1)
      .map(g => ({ ...g, count: colorCardsMap.get(g.name)?.size || 0 }));
  }, [colorCardsMap]);

  // ショーケース用
  const showcaseColors = useMemo(() => {
    const top = colorGroupsByCount.slice(0, Math.min(12, colorGroupsByCount.length));
    return pickRandom(top, Math.min(8, top.length));
  }, [colorGroupsByCount]);

  // 選択した色グループのカード（色の支配度でソート）
  const filteredCards = useMemo(() => {
    if (!colorName) return [];
    const cardIds = colorCardsMap.get(colorName);
    if (!cardIds) return [];
    const cards = allCards.filter(c => cardIds.has(c.id));

    // mainColorsの先頭に近いほどスコアが高い
    const scored = cards.map(card => {
      const colors = card.characterData?.mainColors || [];
      let bestIndex = colors.length;
      colors.forEach((hex, i) => {
        if (classifyColor(hex) === colorName && i < bestIndex) bestIndex = i;
      });
      // その色が何個含まれてるか
      const matchCount = colors.filter(hex => classifyColor(hex) === colorName).length;
      return { card, score: bestIndex * 10 - matchCount };
    });
    scored.sort((a, b) => a.score - b.score);
    return scored.map(s => s.card);
  }, [colorName, allCards, colorCardsMap]);

  // 関連色（同じカードによく一緒に出る色）
  const relatedColors = useMemo(() => {
    if (!colorName) return [];
    const cooccurrence = new Map<string, number>();
    filteredCards.forEach(card => {
      const groups = new Set<string>();
      card.characterData?.mainColors?.forEach(hex => groups.add(classifyColor(hex)));
      groups.delete(colorName);
      groups.forEach(g => cooccurrence.set(g, (cooccurrence.get(g) || 0) + 1));
    });
    const hueOrder = COLOR_GROUPS.map(g => g.name);
    return Array.from(cooccurrence.entries())
      .map(([name, count]) => {
        const group = COLOR_GROUPS.find(g => g.name === name);
        return { name, hex: group?.hex || '#888', count };
      })
      .sort((a, b) => hueOrder.indexOf(a.name) - hueOrder.indexOf(b.name));
  }, [colorName, filteredCards]);

  useEffect(() => {
    const fetchCards = async () => {
      if (!db) { setIsLoading(false); return; }
      try {
        const cardsQuery = query(
          collection(db, 'cards'),
          where('userId', '==', OWNER_UID),
          orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(cardsQuery);
        setAllCards(processFirestoreCardsLight(snapshot.docs, { includePrivate: true }));
      } catch (error) {
        console.error('Error fetching cards:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCards();
  }, []);

  const getColorGroup = (name: string) => COLOR_GROUPS.find(g => g.name === name);

  return (
    <div className="min-h-screen p-4 sm:p-8 font-sans flex flex-col bg-theme-primary">
      <Header />

      <main className="flex-grow">
        {/* ヘッダー */}
        <div className="mb-4">
          {colorName ? (
            <div className="flex items-center -mr-4 sm:-mr-8">
              <div className="flex-shrink-0 flex items-center space-x-2 mr-3">
                <button
                  onClick={() => navigate('/colors')}
                  className="text-theme-tertiary hover:text-brand-primary transition-colors text-sm"
                >
                  colors
                </button>
                <span className="text-theme-tertiary">/</span>
                <div
                  className="w-5 h-5 rounded-md border border-theme-primary"
                  style={{ backgroundColor: getColorGroup(colorName)?.hex || '#888' }}
                />
                <h2 className="text-xl fira-sans-extra-bold-italic text-theme-primary whitespace-nowrap">
                  {colorName}
                  <span className="text-sm text-theme-tertiary font-normal ml-2">
                    {filteredCards.length || ''}
                  </span>
                </h2>
              </div>

              {/* 関連色 */}
              {relatedColors.length > 0 && (
                <div className="flex overflow-x-auto overflow-y-visible scrollbar-hide space-x-1.5 pr-4 sm:pr-8 py-1">
                  {relatedColors.map(c => (
                    <button
                      key={c.name}
                      onClick={() => navigate(`/colors/${c.name}`)}
                      className="flex-shrink-0 w-6 h-6 rounded-md border border-theme-primary hover:scale-125 transition-transform"
                      style={{ backgroundColor: c.hex }}
                      aria-label={c.name}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => navigate('/')}
                className="text-theme-tertiary hover:text-brand-primary transition-colors text-sm"
              >
                home
              </button>
              <span className="text-theme-tertiary">/</span>
              <h2 className="text-xl fira-sans-extra-bold-italic text-theme-primary">
                colors
              </h2>
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={`skeleton-${i}`} />
            ))}
          </div>
        ) : colorName ? (
          /* 個別カラーページ */
          filteredCards.length === 0 ? (
            <div className="flex justify-center py-16">
              <div className="text-theme-tertiary">No cards found for "{colorName}"</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-8">
              {filteredCards.map(card => (
                <Card key={card.id} card={card} onGenerate={() => {}} onDelete={() => {}} />
              ))}
            </div>
          )
        ) : (
          /* カラー一覧ページ: ショーケース + 全色 */
          <div className="space-y-6">
            {showcaseColors.map(color => {
              const cardIds = colorCardsMap.get(color.name);
              const matchingCards = cardIds ? allCards.filter(c => cardIds.has(c.id)) : [];
              // 色の支配度でソート
              const cards = matchingCards
                .map(card => {
                  const colors = card.characterData?.mainColors || [];
                  let bestIdx = colors.length;
                  colors.forEach((hex, i) => { if (classifyColor(hex) === color.name && i < bestIdx) bestIdx = i; });
                  const matchCount = colors.filter(hex => classifyColor(hex) === color.name).length;
                  return { card, score: bestIdx * 10 - matchCount };
                })
                .sort((a, b) => a.score - b.score)
                .slice(0, 5)
                .map(s => s.card);
              return (
                <div key={color.name} className="sm:mx-0 -mx-4">
                  <div className="flex gap-3 overflow-x-auto sm:overflow-hidden scrollbar-hide px-4 sm:px-0">
                    {/* カラー名カード */}
                    <button
                      onClick={() => navigate(`/colors/${color.name}`)}
                      className={`flex-shrink-0 w-36 sm:w-[calc((100%-3*0.75rem)/4)] md:w-[calc((100%-4*0.75rem)/5)] lg:w-[calc((100%-4*0.75rem)/5)] xl:w-[calc((100%-5*0.75rem)/6)] aspect-[3/4] rounded-lg relative transition-colors overflow-hidden`}
                      style={{ backgroundColor: color.hex }}
                    >
                      <div className="absolute bottom-4 left-4 right-2 sm:right-auto overflow-hidden text-left">
                        <div className="text-2xl fira-sans-extra-bold-italic leading-tight text-white drop-shadow-lg break-all sm:break-normal">
                          {color.name}
                        </div>
                        <div className="text-sm fira-sans-book-italic mt-1 text-white/80 drop-shadow">
                          {color.count} cards
                        </div>
                      </div>
                    </button>

                    {/* カード */}
                    {cards.map(card => (
                      <div key={card.id} className="flex-shrink-0 w-40 sm:w-[calc((100%-3*0.75rem)/4)] md:w-[calc((100%-4*0.75rem)/5)] lg:w-[calc((100%-4*0.75rem)/5)] xl:w-[calc((100%-5*0.75rem)/6)]">
                        <Card card={card} onGenerate={() => {}} onDelete={() => {}} />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* 全色一覧 */}
            <div>
              <h3 className={`text-sm font-bold uppercase tracking-wider mb-4 ${
                theme === 'dark' ? 'text-zinc-500' : 'text-theme-tertiary'
              }`}>
                All Colors
              </h3>
              <div className="flex flex-wrap gap-2">
                {colorGroupsHue.map(color => (
                  <button
                    key={color.name}
                    onClick={() => navigate(`/colors/${color.name}`)}
                    className="flex items-center space-x-1.5 px-3 py-1.5 text-sm rounded-full bg-theme-secondary text-theme-secondary hover:bg-brand-primary hover:text-white transition-colors"
                  >
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: color.hex }}
                    />
                    <span>{color.name}</span>
                    <span className="text-xs opacity-70">{color.count}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ColorPage;
